import { useCallback, useEffect, useState } from "react";
import { api, errMsg } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { PageHeader } from "@/components/shared";
import {
  AdminAuditTable, AdminErrorsTable, AdminKbPanel, AdminPetsTable, AdminRevenuePanel, AdminRunsPanel,
  AdminTicketsPanel, AdminUsersTable, StatCard,
} from "@/components/admin-panels";
import { AlertTriangle, BookOpen, Bot, DollarSign, FileSearch, Gift, PawPrint, Ticket, Users } from "lucide-react";

export default function Admin() {
  const [metrics, setMetrics] = useState(null);
  const [users, setUsers] = useState([]);
  const [audit, setAudit] = useState([]);
  const [runs, setRuns] = useState([]);
  const [tickets, setTickets] = useState([]);
  const [kb, setKb] = useState([]);
  const [health, setHealth] = useState(null);
  const [kbOpen, setKbOpen] = useState(false);
  const [kbForm, setKbForm] = useState({ title: "", category: "general", content: "" });
  const [pets, setPets] = useState([]);
  const [revenue, setRevenue] = useState(null);
  const [clientErrors, setClientErrors] = useState([]);
  const [replyFor, setReplyFor] = useState(null); // ticket being replied to
  const [replyText, setReplyText] = useState("");
  const [giftFor, setGiftFor] = useState(null); // user being gifted
  const [giftForm, setGiftForm] = useState({ aiCredits: "", freeMonths: "", compPlan: "premium" });

  const load = useCallback(async () => {
    try {
      const [m, u, a, r, h, k, t, p, rev, ce] = await Promise.all([
        api.get("/admin/metrics"), api.get("/admin/users"), api.get("/audit-logs?limit=100"),
        api.get("/agents/runs"), api.get("/admin/system-health"), api.get("/knowledge-articles?limit=100"),
        api.get("/support-tickets?limit=100"), api.get("/admin/pets"), api.get("/admin/revenue"),
        api.get("/admin/client-errors"),
      ]);
      setMetrics(m.data); setUsers(u.data); setAudit(a.data); setRuns(r.data); setHealth(h.data); setKb(k.data); setTickets(t.data);
      setPets(p.data); setRevenue(rev.data); setClientErrors(ce.data);
    } catch (e) { toast.error(errMsg(e)); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const toggleActive = async (u) => {
    try { await api.patch(`/admin/users/${u.id}`, { isActive: !u.isActive }); toast.success(`${u.name} ${u.isActive ? "disabled" : "enabled"}.`); load(); } catch (e) { toast.error(errMsg(e)); }
  };
  const changeRole = async (u, role) => {
    try { await api.patch(`/admin/users/${u.id}`, { role }); toast.success("Role updated."); load(); } catch (e) { toast.error(errMsg(e)); }
  };
  const changePlan = async (u, plan) => {
    try { await api.patch(`/admin/users/${u.id}`, { plan }); toast.success(`Plan set to ${plan}.`); load(); } catch (e) { toast.error(errMsg(e)); }
  };
  const updateTicket = async (t, status) => {
    try { await api.patch(`/admin/tickets/${t.id}`, { status }); toast.success("Ticket updated."); load(); } catch (e) { toast.error(errMsg(e)); }
  };
  const sendReply = async () => {
    if (!replyText.trim()) { toast.error("Reply message required."); return; }
    try {
      await api.post(`/admin/tickets/${replyFor.id}/reply`, { message: replyText });
      toast.success("Reply sent — the user has been notified.");
      setReplyFor(null); setReplyText("");
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };
  const sendGift = async () => {
    const aiCredits = parseInt(giftForm.aiCredits, 10) || 0;
    const freeMonths = parseInt(giftForm.freeMonths, 10) || 0;
    if (aiCredits <= 0 && freeMonths <= 0) { toast.error("Enter AI credits and/or free months."); return; }
    try {
      await api.post(`/admin/users/${giftFor.id}/grant`, { aiCredits, freeMonths, compPlan: giftForm.compPlan });
      toast.success(`Gift sent to ${giftFor.name} — they've been notified.`);
      setGiftFor(null); setGiftForm({ aiCredits: "", freeMonths: "", compPlan: "premium" });
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };
  const openAttachment = async (id) => {
    try {
      const { data } = await api.get(`/uploads/${id}`, { responseType: "blob" });
      const url = URL.createObjectURL(data);
      window.open(url, "_blank");
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) { toast.error(errMsg(e)); }
  };
  const saveKb = async () => {
    if (!kbForm.title || !kbForm.content) { toast.error("Title and content required."); return; }
    try { await api.post("/knowledge-articles", kbForm); toast.success("Article added."); setKbOpen(false); setKbForm({ title: "", category: "general", content: "" }); load(); } catch (e) { toast.error(errMsg(e)); }
  };
  const sendDigest = async () => {
    try { await api.post("/admin/digest/send"); toast.success("Weekly digest sent to admin inboxes."); } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div>
      <PageHeader title="Admin Dashboard" subtitle="Users, AI usage, escalations, audit trail, and system health.">
        {health && <Badge variant="secondary" className={`border-0 ${health.status === "ok" ? "bg-[hsl(var(--success)/0.12)] text-[hsl(var(--success))]" : "bg-[hsl(var(--destructive)/0.12)] text-[hsl(var(--destructive))]"}`} data-testid="admin-health-badge">System {health.status} · DB {health.database} · AI {health.aiProvider}</Badge>}
      </PageHeader>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
        <StatCard label="MRR (after discounts)" value={revenue ? `$${revenue.mrr}` : null} testId="admin-stat-mrr" />
        <StatCard label="Revenue this month" value={revenue ? `$${revenue.revenueThisMonth}` : null} testId="admin-stat-month-revenue" />
        <StatCard label="Active users" value={metrics?.activeUsers} testId="admin-stat-users" />
        <StatCard label="Pets" value={metrics?.totalPets} testId="admin-stat-pets" />
        <StatCard label="AI usage calls" value={metrics?.aiUsageCalls} testId="admin-stat-ai" />
        <StatCard label="Open tickets" value={metrics?.openTickets} testId="admin-stat-tickets" />
        <StatCard label="Beta testers" value={revenue?.betaTesterCount} testId="admin-stat-beta" />
        <StatCard label="In trial" value={revenue?.trialingCount} testId="admin-stat-trialing" />
      </div>

      <Tabs defaultValue="users">
        <TabsList className="mb-4 flex-wrap h-auto">
          <TabsTrigger value="users" data-testid="admin-users-tab"><Users className="w-3.5 h-3.5 mr-1" /> Users</TabsTrigger>
          <TabsTrigger value="pets" data-testid="admin-pets-tab"><PawPrint className="w-3.5 h-3.5 mr-1" /> Pets</TabsTrigger>
          <TabsTrigger value="revenue" data-testid="admin-revenue-tab"><DollarSign className="w-3.5 h-3.5 mr-1" /> Revenue</TabsTrigger>
          <TabsTrigger value="tickets" data-testid="admin-tickets-tab"><Ticket className="w-3.5 h-3.5 mr-1" /> Tickets</TabsTrigger>
          <TabsTrigger value="errors" data-testid="admin-errors-tab"><AlertTriangle className="w-3.5 h-3.5 mr-1" /> Errors{clientErrors.length ? ` (${clientErrors.length})` : ""}</TabsTrigger>
          <TabsTrigger value="audit" data-testid="admin-audit-tab"><FileSearch className="w-3.5 h-3.5 mr-1" /> Audit Logs</TabsTrigger>
          <TabsTrigger value="runs" data-testid="admin-runs-tab"><Bot className="w-3.5 h-3.5 mr-1" /> Agent Runs</TabsTrigger>
          <TabsTrigger value="kb" data-testid="admin-kb-tab"><BookOpen className="w-3.5 h-3.5 mr-1" /> Knowledge Base</TabsTrigger>
        </TabsList>

        <TabsContent value="users">
          <AdminUsersTable
            users={users}
            onChangeRole={changeRole}
            onChangePlan={changePlan}
            onToggleActive={toggleActive}
            onGift={(u) => { setGiftFor(u); setGiftForm({ aiCredits: "", freeMonths: "", compPlan: "premium" }); }}
          />
        </TabsContent>

        <TabsContent value="pets"><AdminPetsTable pets={pets} /></TabsContent>

        <TabsContent value="revenue"><AdminRevenuePanel revenue={revenue} onSendDigest={sendDigest} /></TabsContent>

        <TabsContent value="tickets">
          <AdminTicketsPanel
            tickets={tickets}
            onReply={(t) => { setReplyFor(t); setReplyText(""); }}
            onUpdateStatus={updateTicket}
            onOpenAttachment={openAttachment}
          />
        </TabsContent>

        <TabsContent value="errors"><AdminErrorsTable errors={clientErrors} /></TabsContent>

        <TabsContent value="audit"><AdminAuditTable audit={audit} /></TabsContent>

        <TabsContent value="runs"><AdminRunsPanel runs={runs} /></TabsContent>

        <TabsContent value="kb"><AdminKbPanel kb={kb} onAdd={() => setKbOpen(true)} /></TabsContent>
      </Tabs>

      <Dialog open={!!giftFor} onOpenChange={(o) => !o && setGiftFor(null)}>
        <DialogContent data-testid="admin-gift-dialog">
          <DialogHeader><DialogTitle>Gift {giftFor?.name}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label>Bonus AI credits</Label>
              <Input type="number" min="0" max="100000" value={giftForm.aiCredits} onChange={(e) => setGiftForm((f) => ({ ...f, aiCredits: e.target.value }))} placeholder="e.g. 100" data-testid="gift-credits-input" />
              <p className="text-[11px] text-muted-foreground">Used automatically once their monthly plan allowance runs out. Never expires.</p>
            </div>
            <div className="space-y-1.5">
              <Label>Free months on subscription</Label>
              <Input type="number" min="0" max="12" value={giftForm.freeMonths} onChange={(e) => setGiftForm((f) => ({ ...f, freeMonths: e.target.value }))} placeholder="e.g. 1" data-testid="gift-months-input" />
              <p className="text-[11px] text-muted-foreground">Subscribers: Stripe billing is paused for that period. Non-subscribers get complimentary plan access:</p>
              <Select value={giftForm.compPlan} onValueChange={(v) => setGiftForm((f) => ({ ...f, compPlan: v }))}>
                <SelectTrigger className="w-[160px] h-8" data-testid="gift-comp-plan-select"><SelectValue /></SelectTrigger>
                <SelectContent>{["basic", "pro", "premium"].map((p) => <SelectItem key={p} value={p} className="capitalize">{p}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter><Button onClick={sendGift} className="rounded-xl" data-testid="gift-send-button"><Gift className="w-4 h-4 mr-2" /> Send gift</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!replyFor} onOpenChange={(o) => !o && setReplyFor(null)}>
        <DialogContent data-testid="admin-reply-dialog">
          <DialogHeader><DialogTitle>Reply to: {replyFor?.subject}</DialogTitle></DialogHeader>
          <div className="space-y-1.5">
            <Label>Your response (visible to the user)</Label>
            <Textarea rows={5} value={replyText} onChange={(e) => setReplyText(e.target.value)} placeholder="Thanks for reporting this — here's what we found..." maxLength={4000} data-testid="admin-reply-input" />
          </div>
          <DialogFooter><Button onClick={sendReply} className="rounded-xl" data-testid="admin-reply-send-button">Send reply</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={kbOpen} onOpenChange={setKbOpen}>
        <DialogContent data-testid="admin-kb-dialog">
          <DialogHeader><DialogTitle>New knowledge article</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5"><Label>Title *</Label><Input value={kbForm.title} onChange={(e) => setKbForm({ ...kbForm, title: e.target.value })} data-testid="kb-title-input" /></div>
            <div className="space-y-1.5"><Label>Category</Label><Input value={kbForm.category} onChange={(e) => setKbForm({ ...kbForm, category: e.target.value })} data-testid="kb-category-input" /></div>
            <div className="space-y-1.5"><Label>Content *</Label><Textarea rows={5} value={kbForm.content} onChange={(e) => setKbForm({ ...kbForm, content: e.target.value })} data-testid="kb-content-input" /></div>
          </div>
          <DialogFooter><Button onClick={saveKb} className="rounded-xl" data-testid="kb-save-button">Save article</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
